(function () {
  const money = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  const wholeEuro = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
  const stockKeyPrefix = 'metzger_stock_v3_';

  function cents(value) {
    return money.format((Number(value) || 0) / 100);
  }

  function parseJson(root) {
    const node = root.querySelector('[data-metzger-selector-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[Metzgerpaket] Could not parse selector JSON', error);
      return null;
    }
  }

  function showToast(title, message) {
    document.querySelectorAll('.r-metzger-toast').forEach((toast) => toast.remove());
    const toast = document.createElement('div');
    toast.className = 'r-metzger-toast';
    toast.innerHTML = `<strong>${title}</strong><p>${message}</p>`;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 220);
    }, 5200);
  }

  function nextDeliveryLabel() {
    const today = new Date();
    const year = today.getMonth() === 11 ? today.getFullYear() + 1 : today.getFullYear();
    const month = today.getMonth() === 11 ? 0 : today.getMonth() + 1;
    const date = new Date(year, month, 1);
    while (![1, 2, 3].includes(date.getDay())) {
      date.setDate(date.getDate() + 1);
    }
    return date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' });
  }

  function stockForPackage(pkg) {
    const key = `${stockKeyPrefix}${pkg.id}`;
    const existing = window.localStorage.getItem(key);
    if (existing) return Number(existing);
    const base = Number(pkg.initialStock) || 23;
    const stock = Math.max(3, base - Math.floor(Math.random() * 5));
    window.localStorage.setItem(key, String(stock));
    return stock;
  }

  function intervalPriority(plan) {
    const text = `${plan.name || ''} ${plan.description || ''}`.toLowerCase();
    if (text.includes('4') || text.includes('monat')) return 0;
    if (text.includes('8')) return 1;
    if (text.includes('12')) return 2;
    return 5;
  }

  class MetzgerSelector {
    constructor(root) {
      this.root = root;
      this.data = parseJson(root);
      if (!this.data) return;

      this.packageButtons = Array.from(root.querySelectorAll('[data-package-id]'));
      this.sizeTarget = root.querySelector('[data-size-options]');
      this.intervalTarget = root.querySelector('[data-interval-options]');
      this.totalTarget = root.querySelector('[data-total-price]');
      this.portionTarget = root.querySelector('[data-portion-price]');
      this.savingsSummaryTarget = root.querySelector('[data-savings-summary]');
      this.deliveryTarget = root.querySelector('[data-next-delivery]');
      this.stockTarget = root.querySelector('[data-stock-left]');
      this.addButton = root.querySelector('[data-add-to-cart]');

      this.state = {
        packageId: this.packageButtons[0]?.dataset.packageId,
        variantId: null,
        sellingPlanId: null
      };

      this.deliveryTarget.textContent = nextDeliveryLabel();
      this.bind();
      this.selectPackage(this.state.packageId);
    }

    bind() {
      this.packageButtons.forEach((button) => {
        const trigger = button.querySelector('[data-package-pick]') || button;
        trigger.addEventListener('click', () => this.selectPackage(button.dataset.packageId));
      });

      this.addButton.addEventListener('click', () => this.addToCart());
      window.addEventListener('metzger-quiz-complete', (event) => {
        if (event.detail) {
          window.localStorage.setItem('metzger_quiz_gift', JSON.stringify(event.detail));
        }
      });
    }

    get selectedPackage() {
      return this.data.packages[this.state.packageId];
    }

    get selectedVariant() {
      return this.selectedPackage?.variants.find((variant) => String(variant.id) === String(this.state.variantId));
    }

    get selectedPlan() {
      return this.selectedVariant?.sellingPlans.find((plan) => String(plan.id) === String(this.state.sellingPlanId));
    }

    selectPackage(packageId) {
      this.state.packageId = packageId;
      this.packageButtons.forEach((button) => {
        const isSelected = button.dataset.packageId === packageId;
        button.classList.toggle('is-selected', isSelected);
        const trigger = button.querySelector('[data-package-pick]');
        if (trigger) trigger.setAttribute('aria-pressed', String(isSelected));
      });

      const firstVariant = this.selectedPackage?.variants.find((variant) => variant.available) || this.selectedPackage?.variants[0];
      this.state.variantId = firstVariant?.id || null;
      this.renderSizes();
      this.renderIntervals();
      this.updateSummary();
    }

    selectVariant(variantId) {
      this.state.variantId = variantId;
      this.renderSizes();
      this.renderIntervals();
      this.updateSummary();
    }

    selectPlan(planId) {
      this.state.sellingPlanId = planId;
      this.renderIntervals();
      this.updateSummary();
    }

    renderSizes() {
      const variants = this.selectedPackage?.variants || [];
      this.sizeTarget.innerHTML = '';

      variants.forEach((variant) => {
        const savings = this.sizeSavingsLabel(variant);
        const comparisonPrice = this.comparisonPriceForVariant(variant);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'r-metzger-selector__size size-option';
        button.disabled = !variant.available;
        button.classList.toggle('is-selected', String(variant.id) === String(this.state.variantId));
        button.innerHTML = `
          <span class="r-metzger-selector__option-row">
            <span class="r-metzger-selector__option-title">${variant.title}</span>
            <span class="r-metzger-selector__option-price">Ohne Abo ${cents(comparisonPrice)}</span>
          </span>
          <span class="r-small">${variant.detail || this.weightLabel(variant.weight)}</span>
          ${savings ? `<span class="r-metzger-selector__option-proof">${savings}</span>` : ''}
        `;
        button.addEventListener('click', () => this.selectVariant(variant.id));
        this.sizeTarget.appendChild(button);
      });
    }

    renderIntervals() {
      const plans = [...(this.selectedVariant?.sellingPlans || [])].sort((a, b) => intervalPriority(a) - intervalPriority(b));
      if (!plans.length) {
        this.state.sellingPlanId = null;
        this.intervalTarget.innerHTML = '<p class="r-small">Für diese Variante ist aktuell kein Abo-Intervall aktiv.</p>';
        return;
      }

      if (!plans.some((plan) => String(plan.id) === String(this.state.sellingPlanId))) {
        this.state.sellingPlanId = plans[0].id;
      }

      this.intervalTarget.innerHTML = '';
      plans.forEach((plan) => {
        const savings = Math.max(0, this.comparisonPriceForVariant(this.selectedVariant) - (Number(plan.price) || 0));
        const context = savings ? `Du sparst ${cents(savings)} pro Lieferung` : 'Abo-Preis pro Lieferung';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'r-metzger-selector__interval';
        button.classList.toggle('is-selected', String(plan.id) === String(this.state.sellingPlanId));
        button.innerHTML = `
          <span class="r-metzger-selector__option-row">
            <span class="r-metzger-selector__option-title">${plan.name}</span>
            <span class="r-metzger-selector__option-price">${cents(plan.price)}</span>
          </span>
          <span class="r-small">${context}</span>
        `;
        button.addEventListener('click', () => this.selectPlan(plan.id));
        this.intervalTarget.appendChild(button);
      });
    }

    updateSummary() {
      const variant = this.selectedVariant;
      const plan = this.selectedPlan;
      const price = plan?.price || variant?.price || 0;
      const grams = Number(variant?.weight) || this.weightFromTitle(variant?.title);
      const portionSize = Number(this.data.portionSize) || 150;
      const portionCount = grams ? grams / portionSize : 0;
      const portionPrice = portionCount ? price / portionCount : 0;

      this.totalTarget.textContent = cents(price);
      this.portionTarget.textContent = portionPrice ? `ca. ${cents(portionPrice)} pro Portion` : 'Portionspreis nach Gewicht';
      if (this.savingsSummaryTarget) {
        this.savingsSummaryTarget.textContent = this.annualSavingsLabel(variant, plan);
      }
      this.stockTarget.textContent = `${stockForPackage(this.selectedPackage)} Pakete verfügbar`;
      this.addButton.disabled = !variant || !variant.available || !plan;
    }

    weightLabel(weight) {
      const grams = Number(weight) || 0;
      if (!grams) return 'Monatlich kuratierte Auswahl';
      if (grams >= 1000) return `${String(grams / 1000).replace('.', ',')} kg Gesamtgewicht`;
      return `${grams} g Gesamtgewicht`;
    }

    weightFromTitle(title = '') {
      const match = title.match(/(\d+(?:[,.]\d+)?)\s*kg/i);
      return match ? Number(match[1].replace(',', '.')) * 1000 : 0;
    }

    sizeSavingsLabel(variant) {
      const amount = this.savingsAmountForVariant(variant);
      if (!amount) return '';
      return `Spare bis zu ${wholeEuro.format(amount)} Euro gegenüber Einzelkauf`;
    }

    monthlyPlanPriceForVariant(variant) {
      const plans = variant?.sellingPlans || [];
      const monthlyPlan = plans.find((plan) => {
        const text = `${plan.name || ''} ${plan.description || ''}`.toLowerCase();
        return text.includes('monatlich') || text.includes('alle 4') || text.includes('4 wochen');
      });
      return Number(monthlyPlan?.price) || Number(plans[0]?.price) || 0;
    }

    comparisonPriceForVariant(variant) {
      const monthlyPrice = this.monthlyPlanPriceForVariant(variant);
      const savings = this.savingsAmountForVariant(variant) * 100;
      return monthlyPrice && savings ? monthlyPrice + savings : Number(variant?.price) || 0;
    }

    savingsAmountForVariant(variant) {
      if (!variant) return 0;
      const grams = Number(variant.weight) || this.weightFromTitle(variant.title);
      if (!grams) return 0;
      return Number(grams >= 3300 ? this.data.savings?.largeAmount : this.data.savings?.smallAmount) || 0;
    }

    deliveriesPerYear(plan) {
      const text = `${plan?.name || ''} ${plan?.description || ''}`.toLowerCase();
      if (text.includes('viertel') || text.includes('12')) return 4;
      if (text.includes('2 monat') || text.includes('8')) return 6;
      if (text.includes('monat') || text.includes('4')) return 12;
      return 12;
    }

    annualSavingsLabel(variant, plan) {
      const perDelivery = Math.max(0, this.comparisonPriceForVariant(variant) - (Number(plan?.price) || 0)) / 100;
      if (!perDelivery || !plan) return '';
      const annualSavings = perDelivery * this.deliveriesPerYear(plan);
      const prefix = this.data.savings?.annualPrefix || 'Im Jahr';
      return `Spart ${wholeEuro.format(perDelivery)} Euro je Lieferung. ${prefix} bis zu ${wholeEuro.format(annualSavings)} Euro.`;
    }

    quizGiftProperties() {
      const raw = window.localStorage.getItem('metzger_quiz_gift');
      if (!raw) return {};
      try {
        const gift = JSON.parse(raw);
        return {
          '_Quiz Geschenk': gift.quiz_gift_title || gift.giftTitle || gift.title || gift.gift || 'Erster-Bestellbonus',
          '_Quiz Teilnahme': 'ja'
        };
      } catch (_error) {
        return {};
      }
    }

    quizGiftAttributes() {
      const raw = window.localStorage.getItem('metzger_quiz_gift');
      if (!raw) return null;
      try {
        const gift = JSON.parse(raw);
        return {
          first_order_gift: gift.first_order_gift || 'true',
          quiz_completed: gift.quiz_completed || 'true',
          quiz_gift_title: gift.quiz_gift_title || gift.title || 'Erster-Bestellbonus',
          quiz_gift_recommendation: gift.quiz_gift_recommendation || gift.quiz_gift_title || gift.title || 'Erster-Bestellbonus',
          quiz_gift_handle: gift.quiz_gift_handle || gift.handle || '',
          quiz_gift_variant: String(gift.quiz_gift_variant || gift.variantId || ''),
          quiz_gift_category: gift.quiz_gift_category || gift.category || '',
          quiz_answers_digest: gift.quiz_answers_digest || '',
          mystery_gift: gift.mystery_gift || 'true',
          staff_order_summary: gift.staff_order_summary || 'ERSTE BESTELLUNG - MYSTERY GESCHENK BEILEGEN'
        };
      } catch (_error) {
        return null;
      }
    }

    async addToCart() {
      const variant = this.selectedVariant;
      const plan = this.selectedPlan;
      if (!variant || !plan) {
        showToast('Abo noch nicht komplett', 'Bitte wähle Paket, Größe und Lieferintervall.');
        return;
      }

      this.addButton.disabled = true;
      this.addButton.textContent = 'Wird vorbereitet...';

      try {
        const cart = await fetch('/cart.js').then((response) => response.json());
        if (cart.item_count > 0) {
          showToast('Metzgerpaket einzeln bestellen', this.data.cartRestrictionMessage);
          await fetch('/cart/clear.js', { method: 'POST' });
        }

        const quizAttributes = this.quizGiftAttributes();
        if (quizAttributes) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: quizAttributes })
          });
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variant.id,
              quantity: 1,
              selling_plan: plan.id,
              properties: {
                '_Paket': this.selectedPackage.title,
                '_Lieferintervall': plan.name,
                ...this.quizGiftProperties()
              }
            }]
          })
        });

        if (!response.ok) throw new Error('Cart add failed');
        window.location.href = '/checkout';
      } catch (error) {
        console.error('[Metzgerpaket] Add to cart failed', error);
        showToast('Das hat nicht geklappt', 'Bitte probiere es noch einmal oder melde dich direkt bei uns.');
        this.addButton.disabled = false;
        this.addButton.textContent = 'Metzgerpaket-Abo starten';
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-metzger-selector]').forEach((root) => new MetzgerSelector(root));
  });
})();
